import { canSeePath } from 'constants/navPermissions';
import { RBAC_ENABLED } from 'constants/rbacConfig';

/**
 * Filtering a sidebar down to what the signed-in role can actually open.
 *
 * Shared by both portals. Their nav configs look different on the surface —
 * admin/config/navigation.js groups by `group` and nests with `children`,
 * partner/config/navigation.jsx groups by `section` and does not nest — but both
 * are the same three-level shape: group -> item -> optional child. One walker
 * handles both, which is what keeps the two sidebars from drifting into two
 * slightly different ideas of "visible".
 *
 * The filter is PURE and returns new arrays; it never mutates the config, which
 * is a module-level singleton shared by every render and by the header's
 * title lookup.
 */

/**
 * Re-exported from constants/rbacConfig so the several modules that gate on it
 * cannot end up reading two different environment variables. That file carries
 * the reasoning, including why an unset value means ON.
 */
export const RBAC_NAV_ENABLED = RBAC_ENABLED;

/**
 * Should this leaf render?
 *
 * An item with no `path` is structural — a collapsible parent, a section
 * heading — and is judged by its children, not by itself.
 */
const leafVisible = (perms, item) => (item?.path ? canSeePath(perms, item.path) : true);

/**
 * Filter a nav config to the items `perms` can reach.
 *
 * Empty parents and empty groups are dropped, not left as dead furniture: a
 * "Revenue" caret that expands onto nothing, or a "Settings" heading with no rows
 * under it, reads as a broken portal rather than a scoped one.
 *
 * Returns the config UNCHANGED when the switch is off or the permission list is
 * empty. The empty-list case is the important one — a session minted before the
 * login response carried permissions, or a role nobody has granted anything, must
 * fall through to server enforcement rather than render an empty sidebar. That is
 * the same fail-open rule `canSeePath` applies, restated here so a caller reading
 * only this file still sees it.
 */
export const filterNavigation = (groups, perms) => {
  if (!RBAC_ENABLED) return groups;
  if (!perms || !perms.list?.length) return groups;

  const filtered = [];
  for (const group of groups) {
    const items = [];
    for (const item of group.items || []) {
      if (item.children) {
        const children = item.children.filter((child) => leafVisible(perms, child));
        // A parent whose children are all hidden is hidden too — even if the
        // parent itself carries a path, because that path is only ever a
        // redirect onto one of the children.
        if (children.length) items.push({ ...item, children });
        continue;
      }
      if (leafVisible(perms, item)) items.push(item);
    }
    if (items.length) filtered.push({ ...group, items });
  }
  return filtered;
};

/**
 * The first path the viewer can actually open, for "where do I send them" cases —
 * a denied deep link, or a dashboard the role does not include.
 *
 * Returns `undefined` when nothing is visible at all, which the caller has to
 * handle: sending someone in a loop between two screens they cannot see is worse
 * than showing them the 403 they already have open.
 */
export const firstVisiblePath = (groups, perms) => {
  for (const group of filterNavigation(groups, perms)) {
    for (const item of group.items || []) {
      if (item.path) return item.path;
      const child = (item.children || []).find((candidate) => candidate.path);
      if (child) return child.path;
    }
  }
  return undefined;
};
